"use client";

import {
  BadgePercent,
  ChevronRight,
  CreditCard,
  Landmark,
  Loader2,
  QrCode,
  Sparkles,
  WalletCards,
} from "lucide-react";
import type { ComponentType } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import type {
  PaymentCapabilities,
  PaymentMethodRecord,
  SelectablePaymentMethodCategory,
} from "@/features/payments/lib";
import { cn } from "@/lib/utils";

export type PaymentMethodDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentMethods: PaymentMethodRecord[];
  paymentCapabilities: PaymentCapabilities;
  onSelectRecommended?: (() => void) | null;
  onSelectRecent: (methodId: number) => void;
  onSelectCategory: (category: SelectablePaymentMethodCategory) => void;
  loadingKey?: string | null;
  disabled?: boolean;
};

type MethodRowProps = {
  title: string;
  subtitle?: string | null;
  badge?: string | null;
  loading?: boolean;
  disabled?: boolean;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
};

const CATEGORY_ROW_COPY: Record<
  SelectablePaymentMethodCategory,
  { title: string; subtitle: string; icon: MethodRowProps["icon"] }
> = {
  upi: {
    title: "UPI Apps & ID",
    subtitle: "UPI apps and saved IDs",
    icon: QrCode,
  },
  card: {
    title: "Credit & Debit Cards",
    subtitle: "Pay securely with your card",
    icon: CreditCard,
  },
  netbanking: {
    title: "Netbanking",
    subtitle: "Pay directly from your bank",
    icon: Landmark,
  },
  wallet: {
    title: "Wallets",
    subtitle: "Use your preferred wallet balance",
    icon: WalletCards,
  },
  paylater: {
    title: "Pay Later",
    subtitle: "BNPL & cardless EMI options",
    icon: BadgePercent,
  },
  emi: {
    title: "EMI",
    subtitle: "Split payment into monthly installments",
    icon: BadgePercent,
  },
};

function MethodRow({
  title,
  subtitle,
  badge,
  loading = false,
  disabled = false,
  icon: Icon,
  onClick,
}: MethodRowProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      className={cn(
        "flex w-full touch-manipulation items-center justify-between gap-3 rounded-[22px] border border-border bg-surface px-3.5 py-3 text-left transition-colors hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:outline-none dark:border-white/[0.12] dark:bg-glass-bg-03 dark:hover:bg-glass-bg-06 dark:focus-visible:ring-white/30",
        isDisabled && "cursor-not-allowed opacity-55",
      )}
      disabled={isDisabled}
      onClick={onClick}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-border bg-surface-alt">
          <Icon
            className="h-4.5 w-4.5 text-text-secondary dark:text-white/85"
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0">
          <div className="truncate text-base font-medium text-text-primary dark:text-white">
            {title}
          </div>
          {subtitle ? (
            <div className="truncate text-sm text-text-secondary dark:text-white/60">
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>

      <div className="shrink-0 text-text-secondary dark:text-white/70">
        {loading ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-surface-alt px-2.5 py-1 text-[11px] font-medium tracking-wide uppercase dark:border-white/[0.2] dark:bg-glass-bg-06">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Opening…
          </span>
        ) : badge ? (
          <span className="rounded-full border border-success-border bg-success-bg px-2.5 py-1 text-[11px] font-medium tracking-wide text-success uppercase dark:border-emerald-300/30 dark:bg-emerald-300/12 dark:text-emerald-200">
            {badge}
          </span>
        ) : (
          <ChevronRight
            className="h-4.5 w-4.5 text-text-tertiary dark:text-white/55"
            aria-hidden="true"
          />
        )}
      </div>
    </button>
  );
}

export function PaymentMethodDrawer({
  open,
  onOpenChange,
  paymentMethods,
  paymentCapabilities,
  onSelectRecommended,
  onSelectRecent,
  onSelectCategory,
  loadingKey = null,
  disabled = false,
}: PaymentMethodDrawerProps) {
  const recommendedMethods = paymentMethods.slice(0, 3);
  const availableCategories = paymentCapabilities.availableCategories;
  const moreWays = availableCategories.filter(
    (category) => category !== "upi" && category !== "card",
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent className="mx-auto max-h-[82dvh] max-w-[520px] rounded-t-[30px] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#090b10] dark:text-white">
        <DrawerHeader className="px-4 pb-2 text-left">
          <DrawerTitle className="text-2xl font-semibold text-pretty text-text-primary dark:text-white">
            Choose Payment Method
          </DrawerTitle>
          <DrawerDescription className="text-sm text-text-secondary dark:text-white/60">
            Use your recent method or pick another way to pay.
          </DrawerDescription>
        </DrawerHeader>

        <div className="space-y-5 overflow-y-auto overscroll-contain px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
          {recommendedMethods.length > 0 ? (
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-[13px] font-medium tracking-[0.14em] text-text-tertiary uppercase dark:text-white/55">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                Recommended
              </div>

              {recommendedMethods.map((method, index) => (
                <MethodRow
                  key={method.id}
                  title={method.label}
                  subtitle={method.subtitle ?? "Recent payment method"}
                  badge={index === 0 ? "Best Match" : null}
                  disabled={disabled}
                  icon={method.category === "upi" ? QrCode : CreditCard}
                  loading={loadingKey === `recent:${method.id}`}
                  onClick={() => onSelectRecent(method.id)}
                />
              ))}

              {onSelectRecommended ? (
                <MethodRow
                  title="Use Best Recommended Method"
                  subtitle={recommendedMethods[0]?.label ?? "Recent method"}
                  badge={null}
                  disabled={disabled}
                  icon={Sparkles}
                  loading={loadingKey === "recommended"}
                  onClick={onSelectRecommended}
                />
              ) : null}
            </section>
          ) : null}

          {availableCategories.includes("upi") ? (
            <section className="space-y-2">
              <h3 className="text-[13px] font-medium tracking-[0.14em] text-text-tertiary uppercase dark:text-white/55">
                UPI
              </h3>
              <MethodRow
                title={CATEGORY_ROW_COPY.upi.title}
                subtitle={CATEGORY_ROW_COPY.upi.subtitle}
                disabled={disabled}
                icon={CATEGORY_ROW_COPY.upi.icon}
                loading={loadingKey === "category:upi"}
                onClick={() => onSelectCategory("upi")}
              />
            </section>
          ) : null}

          {availableCategories.includes("card") ? (
            <section className="space-y-2">
              <h3 className="text-[13px] font-medium tracking-[0.14em] text-text-tertiary uppercase dark:text-white/55">
                Cards
              </h3>
              <MethodRow
                title={CATEGORY_ROW_COPY.card.title}
                subtitle={CATEGORY_ROW_COPY.card.subtitle}
                disabled={disabled}
                icon={CATEGORY_ROW_COPY.card.icon}
                loading={loadingKey === "category:card"}
                onClick={() => onSelectCategory("card")}
              />
            </section>
          ) : null}

          {moreWays.length > 0 ? (
            <section className="space-y-2">
              <h3 className="text-[13px] font-medium tracking-[0.14em] text-text-tertiary uppercase dark:text-white/55">
                More Ways To Pay
              </h3>

              {moreWays.map((category) => (
                <MethodRow
                  key={category}
                  title={CATEGORY_ROW_COPY[category].title}
                  subtitle={CATEGORY_ROW_COPY[category].subtitle}
                  disabled={disabled}
                  icon={CATEGORY_ROW_COPY[category].icon}
                  loading={loadingKey === `category:${category}`}
                  onClick={() => onSelectCategory(category)}
                />
              ))}
            </section>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
